import React, { useState, useMemo } from 'react';
import {
    Box,
    Text,
    Paper,
    TextInput,
    ActionIcon,
    Tooltip,
    Group,
} from '@mantine/core';
import {
    IconSearch,
    IconX,
    IconArrowsMaximize,
    IconArrowsMinimize,
} from '@tabler/icons-react';
import { ScimReferenceDisplay } from './ScimReferenceDisplay';
import { getSingularLabel } from '../../utils/pluralizationUtils';

interface ReferenceListViewerProps {
    references: any[];
    label: string;
    maxHeight?: number;
    searchThreshold?: number; // Show search when count exceeds this
    className?: string;
    sx?: any;
}

export const ReferenceListViewer: React.FC<ReferenceListViewerProps> = ({
    references = [],
    label,
    maxHeight = 200,
    searchThreshold = 10,
    className,
    sx
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expanded, setExpanded] = useState(false);

    // Helper to get a display label from a reference
    const getReferenceLabel = (ref: any): string => {
        if (ref == null) return '';
        if (typeof ref === 'string') return ref;
        if (typeof ref === 'object') {
            return ref.DisplayName || ref.displayName || ref.Name || ref.name || ref.Id || ref.id || JSON.stringify(ref);
        }
        return String(ref);
    };

    // Filter references based on search term
    const filteredReferences = useMemo(() => {
        if (!searchTerm.trim()) return references;
        const q = searchTerm.toLowerCase();
        return references.filter(ref => {
            const label = getReferenceLabel(ref);
            return label.toLowerCase().includes(q);
        });
    }, [references, searchTerm]);

    const showSearch = references.length > searchThreshold;
    const displayCount = filteredReferences.length;
    const totalCount = references.length;
    
    // Get singular form of label for search placeholder
    const singularLabel = getSingularLabel(label);

    return (
        <Box className={className} style={sx}>
            {/* Header with count and expand toggle */}
            <Group justify="space-between" align="center" mb="xs">
                <Text size="sm" c="dimmed">
                    {label} {totalCount > 0 && (
                        <Text component="span" size="sm" c="dimmed" ml={4}>
                            ({displayCount}{searchTerm && displayCount !== totalCount ? ` of ${totalCount}` : ''})
                        </Text>
                    )}
                </Text>
                <Tooltip label={expanded ? 'Collapse' : 'Expand'}>
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() => setExpanded(!expanded)}
                        ml="xs"
                    >
                        {expanded ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
                    </ActionIcon>
                </Tooltip>
            </Group>

            {/* Search */}
            {showSearch && (
                <Box mb="xs">
                    <TextInput
                        w="100%"
                        size="sm"
                        placeholder={`Search ${singularLabel.toLowerCase()}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.currentTarget.value)}
                        leftSection={<IconSearch size={16} />}
                        rightSection={
                            searchTerm ? (
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={() => setSearchTerm('')}
                                    aria-label="clear search"
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            ) : undefined
                        }
                    />
                </Box>
            )}

            {/* References List */}
            <Paper
                withBorder
                p="xs"
                style={{
                    ...(expanded ? {} : { maxHeight }),
                    overflow: 'auto',
                }}
            >
                {filteredReferences.length === 0 ? (
                    <Box p="md" w="100%" ta="center">
                        <Text size="sm" c="dimmed">
                            {searchTerm ? 'No references found' : 'No references'}
                        </Text>
                    </Box>
                ) : (
                    <Group gap={4} wrap="wrap" align="flex-start">
                        {filteredReferences.map((ref: any, index: number) => (
                            <ScimReferenceDisplay
                                key={index}
                                reference={ref}
                                variant="compact"
                                showType={true}
                                showLink={true}
                            />
                        ))}
                    </Group>
                )}
            </Paper>
        </Box>
    );
};
